import { lowerCase } from 'es-toolkit';
import React, { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper, Stack } from '@mui/material';

import { hasProjectHierarchyFeatureFlag } from '@paragon/login-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppSelector } from '../../../hooks';
import { SaveAction, SettingsSaveButtonProps } from './SettingsSaveButton';

interface Props extends Pick<SettingsSaveButtonProps, 'enableSaving' | 'onSave'> {
  userVisibleModelType: string;
  validSaveActions: SaveAction[];
}

export function SaveSettingsSplitButton({ userVisibleModelType, validSaveActions, enableSaving, onSave }: Props) {
  const projectHierarchyEnabled = useAppSelector(hasProjectHierarchyFeatureFlag);

  const [open, setOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(1);

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorElement && anchorElement.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  function getSaveButtonMessage(action: SaveAction) {
    const levelName = userVisibleModelType ? lowerCase(userVisibleModelType) : 'unknown';

    switch (action) {
      case SaveAction.DefaultsForHierarchy:
        return projectHierarchyEnabled
          ? `Change default settings for entire ${levelName}`
          : 'Change default settings for new components only';

      case SaveAction.DefaultsForSingleLevel:
        return `Change default settings for this ${levelName} only`;

      case SaveAction.ActualForHierarchy:
        return projectHierarchyEnabled
          ? `Change default and component settings for entire ${levelName}`
          : 'Change defaults and apply to all components in project';

      case SaveAction.ActualForSingleComponent:
        return 'Change settings for this component';

      default:
        assertExhaustiveSwitch(action);
    }
  }

  return (
    <Stack sx={{ alignItems: 'center' }}>
      {validSaveActions.length === 1 ? (
        <Button
          variant="contained"
          sx={{ py: 1, m: 1, mr: 0 }}
          onClick={() => onSave(validSaveActions[0])}
          disabled={!enableSaving}
        >
          {getSaveButtonMessage(validSaveActions[0])}
        </Button>
      ) : (
        <ButtonGroup ref={setAnchorElement} aria-label="split button">
          <Button
            variant="contained"
            sx={{ py: 1, m: 1, mr: 0 }}
            onClick={() => onSave(validSaveActions[selectedIndex])}
            disabled={!enableSaving}
          >
            {getSaveButtonMessage(validSaveActions[selectedIndex])}
          </Button>
          <Button
            variant="contained"
            sx={{ width: '1%', m: 1, ml: 0 }}
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="select what settings to change"
            aria-haspopup="menu"
            onClick={handleToggle}
            disabled={!enableSaving}
            id="save-settings-option-button"
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
      )}
      <Popper open={open} anchorEl={anchorElement} role={undefined} transition disablePortal sx={{ zIndex: 1000 }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {validSaveActions.map((saveAction, index) => (
                    <MenuItem
                      key={saveAction}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {getSaveButtonMessage(saveAction)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );
}
